import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Session } from '@supabase/supabase-js';
import { isSupabaseConfigured } from '../../lib/supabaseClient';
import {
  checkCurrentUserIsAdmin,
  createPost,
  deletePost,
  fetchAdminPosts,
  getSession,
  onAuthStateChange,
  signInAdmin,
  signOutAdmin,
  updatePost,
  uploadPostImage,
} from '../../services/postsService';
import { Post, PostUpsertInput } from '../../types';

const today = new Date().toISOString().slice(0, 10);

const getInitialFormState = (): PostUpsertInput => ({
  title: '',
  description: '',
  image_path: '',
  date: today,
  link_fb: '',
  link_instagram: '',
  published: true,
});

function AdminPage() {
  const [session, setSession] = useState<Session | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authError, setAuthError] = useState('');

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  const [posts, setPosts] = useState<Post[]>([]);
  const [isPostsLoading, setIsPostsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [formState, setFormState] = useState<PostUpsertInput>(getInitialFormState());
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [selectedImagePreviewUrl, setSelectedImagePreviewUrl] = useState('');

  const editingPost = useMemo(() => {
    if (!editingPostId) {
      return null;
    }

    return posts.find((post) => post.id === editingPostId) || null;
  }, [editingPostId, posts]);

  useEffect(() => {
    if (!selectedImage) {
      setSelectedImagePreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedImage);
    setSelectedImagePreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedImage]);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setIsCheckingAuth(false);
      return undefined;
    }

    let mounted = true;

    getSession()
      .then((currentSession) => {
        if (!mounted) {
          return;
        }

        setSession(currentSession);
      })
      .catch((error) => {
        if (!mounted) {
          return;
        }

        setAuthError(error.message || 'Nie udało się odczytać sesji.');
      })
      .finally(() => {
        if (mounted) {
          setIsCheckingAuth(false);
        }
      });

    const subscription = onAuthStateChange((currentSession) => {
      setSession(currentSession);
      setAuthError('');
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      return;
    }

    if (!session) {
      setIsAdmin(false);
      return;
    }

    let mounted = true;
    setIsCheckingAuth(true);

    checkCurrentUserIsAdmin()
      .then((allowed) => {
        if (!mounted) {
          return;
        }

        setIsAdmin(allowed);
      })
      .catch((error) => {
        if (!mounted) {
          return;
        }

        setAuthError(error.message || 'Nie udało się zweryfikować uprawnień administratora.');
      })
      .finally(() => {
        if (mounted) {
          setIsCheckingAuth(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [session]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    loadAdminPosts();
  }, [isAdmin]);

  const loadAdminPosts = async () => {
    setIsPostsLoading(true);

    try {
      const data = await fetchAdminPosts();
      setPosts(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Nie udało się pobrać postów.';
      setStatusMessage(message);
    } finally {
      setIsPostsLoading(false);
    }
  };

  const handleLoginSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAuthError('');
    setIsLoginLoading(true);

    try {
      await signInAdmin(loginEmail, loginPassword);
      setLoginPassword('');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Logowanie nie powiodło się.';
      setAuthError(message);
    } finally {
      setIsLoginLoading(false);
    }
  };

  const resetForm = () => {
    setEditingPostId(null);
    setFormState(getInitialFormState());
    setSelectedImage(null);
  };

  const handleEditPost = (post: Post) => {
    setEditingPostId(post.id || null);
    setFormState({
      title: post.title,
      description: post.description,
      image_path: post.image_path || '',
      date: post.date,
      link_fb: post.link_fb || '',
      link_instagram: post.link_instagram || '',
      published: post.published ?? true,
    });
    setSelectedImage(null);
    setStatusMessage('');
  };

  const handleDeletePost = async (postId?: string) => {
    if (!postId) {
      return;
    }

    const confirmed = window.confirm('Czy na pewno chcesz usunąć ten post?');
    if (!confirmed) {
      return;
    }

    setStatusMessage('');

    try {
      await deletePost(postId);
      if (editingPostId === postId) {
        resetForm();
      }

      await loadAdminPosts();
      setStatusMessage('Post został usunięty.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Nie udało się usunąć posta.';
      setStatusMessage(message);
    }
  };

  const handleSubmitPost = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formState.title.trim() || !formState.description.trim() || !formState.date) {
      setStatusMessage('Uzupełnij tytuł, opis i datę.');
      return;
    }

    setIsSaving(true);
    setStatusMessage('');

    try {
      let imagePath = formState.image_path;

      if (selectedImage) {
        imagePath = await uploadPostImage(selectedImage);
      }

      if (!imagePath) {
        setStatusMessage('Wgraj obraz posta.');
        return;
      }

      const payload: PostUpsertInput = {
        ...formState,
        image_path: imagePath,
      };

      if (editingPostId) {
        await updatePost(editingPostId, payload);
        setStatusMessage('Post został zaktualizowany.');
      } else {
        await createPost(payload);
        setStatusMessage('Post został dodany.');
      }

      await loadAdminPosts();
      resetForm();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Nie udało się zapisać posta.';
      setStatusMessage(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    setAuthError('');

    try {
      await signOutAdmin();
      setSession(null);
      setIsAdmin(false);
      resetForm();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Nie udało się wylogować.';
      setAuthError(message);
    }
  };

  const previewImageSrc = selectedImagePreviewUrl || editingPost?.image || '';
  const previewTitle = formState.title.trim() || 'Podgląd tytułu posta';
  const previewDescription = formState.description.trim() || 'Tutaj pojawi się opis posta przed zapisaniem.';
  const previewDate = formState.date ? new Date(formState.date).toLocaleDateString('pl-PL') : 'Brak daty';

  if (!isSupabaseConfigured) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-12">
        <div className="mx-auto max-w-2xl rounded-xl bg-white p-8 shadow">
          <h1 className="mb-4 text-2xl font-bold">Panel administratora</h1>
          <p className="text-slate-700">
            Brak konfiguracji Supabase. Ustaw zmienne: <code>REACT_APP_SUPABASE_URL</code> i{' '}
            <code>REACT_APP_SUPABASE_ANON_KEY</code>.
          </p>
        </div>
      </div>
    );
  }

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-12">
        <div className="mx-auto max-w-2xl rounded-xl bg-white p-8 text-center shadow">
          <p className="text-lg font-medium text-slate-700">Sprawdzanie sesji...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-12">
        <div className="mx-auto max-w-md rounded-xl bg-white p-8 shadow">
          <h1 className="mb-6 text-2xl font-bold">Logowanie administratora</h1>
          <form className="space-y-4" onSubmit={handleLoginSubmit}>
            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium text-slate-700">E-mail</label>
              <input
                id="email"
                type="email"
                value={loginEmail}
                onChange={(event) => setLoginEmail(event.target.value)}
                required
                className="w-full rounded-md border border-slate-300 px-3 py-2"
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1 block text-sm font-medium text-slate-700">Hasło</label>
              <input
                id="password"
                type="password"
                value={loginPassword}
                onChange={(event) => setLoginPassword(event.target.value)}
                required
                className="w-full rounded-md border border-slate-300 px-3 py-2"
              />
            </div>
            {authError && <p className="text-sm text-red-600">{authError}</p>}
            <button
              type="submit"
              disabled={isLoginLoading}
              className="w-full rounded-md bg-blue-700 px-4 py-2 font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoginLoading ? 'Logowanie...' : 'Zaloguj się'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-12">
        <div className="mx-auto max-w-2xl rounded-xl bg-white p-8 shadow">
          <h1 className="mb-4 text-2xl font-bold">Brak dostępu</h1>
          <p className="mb-4 text-slate-700">
            To konto nie jest dodane do listy administratorów w tabeli <code>admin_users</code>.
          </p>
          {authError && <p className="mb-4 text-sm text-red-600">{authError}</p>}
          <button
            type="button"
            onClick={handleLogout}
            className="rounded-md bg-slate-800 px-4 py-2 font-medium text-white"
          >
            Wyloguj
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-6 md:py-10">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-col gap-4 rounded-xl bg-white p-6 shadow md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Panel administratora</h1>
            <p className="text-sm text-slate-600">Zarządzanie postami (PL, Supabase)</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="rounded-md bg-slate-800 px-4 py-2 font-medium text-white"
          >
            Wyloguj
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.2fr,1fr]">
          <div className="rounded-xl bg-white p-6 shadow">
            <h2 className="mb-4 text-xl font-semibold">
              {editingPost ? 'Edytuj post' : 'Nowy post'}
            </h2>
            <form className="space-y-4" onSubmit={handleSubmitPost}>
              <div>
                <label htmlFor="title" className="mb-1 block text-sm font-medium text-slate-700">Tytuł</label>
                <input
                  id="title"
                  value={formState.title}
                  onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
                  required
                  className="w-full rounded-md border border-slate-300 px-3 py-2"
                />
              </div>

              <div>
                <label htmlFor="description" className="mb-1 block text-sm font-medium text-slate-700">Opis</label>
                <textarea
                  id="description"
                  value={formState.description}
                  onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
                  required
                  rows={5}
                  className="w-full rounded-md border border-slate-300 px-3 py-2"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="date" className="mb-1 block text-sm font-medium text-slate-700">Data</label>
                  <input
                    id="date"
                    type="date"
                    value={formState.date}
                    onChange={(event) => setFormState((prev) => ({ ...prev, date: event.target.value }))}
                    required
                    className="w-full rounded-md border border-slate-300 px-3 py-2"
                  />
                </div>

                <div className="flex items-center gap-2 pt-7">
                  <input
                    id="published"
                    type="checkbox"
                    checked={formState.published}
                    onChange={(event) => setFormState((prev) => ({ ...prev, published: event.target.checked }))}
                    className="h-4 w-4"
                  />
                  <label htmlFor="published" className="text-sm font-medium text-slate-700">Opublikowany</label>
                </div>
              </div>

              <div>
                <label htmlFor="link_fb" className="mb-1 block text-sm font-medium text-slate-700">Link Facebook (opcjonalnie)</label>
                <input
                  id="link_fb"
                  type="url"
                  value={formState.link_fb || ''}
                  onChange={(event) => setFormState((prev) => ({ ...prev, link_fb: event.target.value }))}
                  className="w-full rounded-md border border-slate-300 px-3 py-2"
                />
              </div>

              <div>
                <label htmlFor="link_instagram" className="mb-1 block text-sm font-medium text-slate-700">Link Instagram (opcjonalnie)</label>
                <input
                  id="link_instagram"
                  type="url"
                  value={formState.link_instagram || ''}
                  onChange={(event) => setFormState((prev) => ({ ...prev, link_instagram: event.target.value }))}
                  className="w-full rounded-md border border-slate-300 px-3 py-2"
                />
              </div>

              <div>
                <label htmlFor="image" className="mb-1 block text-sm font-medium text-slate-700">Zdjęcie posta</label>
                <input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={(event) => setSelectedImage(event.target.files?.[0] || null)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2"
                />
                {editingPost && !selectedImage && (
                  <p className="mt-1 text-xs text-slate-500">Bez wyboru nowego pliku pozostanie obecne zdjęcie.</p>
                )}
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-md bg-blue-700 px-4 py-2 font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSaving ? 'Zapisywanie...' : editingPost ? 'Zapisz zmiany' : 'Dodaj post'}
                </button>

                {editingPost && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-md bg-slate-200 px-4 py-2 font-medium text-slate-800"
                  >
                    Anuluj edycję
                  </button>
                )}
              </div>

              {statusMessage && <p className="text-sm text-slate-700">{statusMessage}</p>}
            </form>
          </div>

          <div className="space-y-6">
            <div className="rounded-xl bg-white p-6 shadow">
              <h2 className="mb-4 text-xl font-semibold">Podgląd przed zapisem</h2>
              <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <div className="h-56 bg-slate-100">
                  {previewImageSrc ? (
                    <img src={previewImageSrc} alt="Podgląd posta" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-slate-500">
                      Brak zdjęcia w podglądzie
                    </div>
                  )}
                </div>
                <div className="space-y-2 p-4">
                  <p className="text-lg font-semibold text-slate-900">{previewTitle}</p>
                  <p className="text-sm text-slate-700">{previewDescription}</p>
                  <p className="text-xs text-slate-500">{previewDate}</p>
                  <p className="text-xs font-medium text-slate-600">
                    {formState.published ? 'Status: Opublikowany' : 'Status: Szkic'}
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {formState.link_fb?.trim() && (
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700">Facebook</span>
                    )}
                    {formState.link_instagram?.trim() && (
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700">Instagram</span>
                    )}
                  </div>
                </div>
              </article>
            </div>

            <div className="rounded-xl bg-white p-6 shadow">
              <h2 className="mb-4 text-xl font-semibold">Lista postów</h2>
              {isPostsLoading ? (
                <p className="text-sm text-slate-600">Wczytywanie postów...</p>
              ) : posts.length === 0 ? (
                <p className="text-sm text-slate-600">Brak postów do wyświetlenia.</p>
              ) : (
                <ul className="space-y-3">
                  {posts.map((post) => (
                    <li key={post.id} className="rounded-lg border border-slate-200 p-3">
                      <p className="font-semibold text-slate-900">{post.title}</p>
                      <p className="text-xs text-slate-500">{new Date(post.date).toLocaleDateString('pl-PL')}</p>
                      <p className="text-xs text-slate-500">{post.published ? 'Opublikowany' : 'Szkic'}</p>
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditPost(post)}
                          className="rounded bg-slate-800 px-3 py-1 text-xs font-medium text-white"
                        >
                          Edytuj
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeletePost(post.id)}
                          className="rounded bg-red-700 px-3 py-1 text-xs font-medium text-white"
                        >
                          Usuń
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
